import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import api from '../utils/api';
import { useToast } from '../App';
import LoadingState from '../components/LoadingState';

const Dashboard = () => {
    const navigate = useNavigate();
    const { showError } = useToast();
    const [loading, setLoading] = useState(true);
    const [initialLoad, setInitialLoad] = useState(true);
    const [stats, setStats] = useState({
        orders: { total: 0, pending: 0, processing: 0, completed: 0, revenue: 0, growth: 0 },
        products: { total: 0, in_stock: 0, out_of_stock: 0, low_stock: 0 },
        vendors: { total: 0, active: 0, pending: 0 },
        sync: { last_sync: null, status: 'idle', pending_operations: 0 }
    });
    const [modes, setModes] = useState({
        databaseMode: true,
        multivendorMode: false
    });
    const [recentActivity, setRecentActivity] = useState([]);
    const [userInfo, setUserInfo] = useState(null);
    const [dateFilter, setDateFilter] = useState({
        period: 'last_7_days', // today, yesterday, last_7_days, last_15_days, last_month, custom
        from: '',
        to: ''
    });
    const [revenueData, setRevenueData] = useState([]);
    const [topProducts, setTopProducts] = useState([]);

    const isFrontend = !window.location.pathname.includes('wp-admin');

    useEffect(() => {
        loadDashboardData();
        setInitialLoad(false);
    }, []);

    // Watch for date filter changes and reload WooCommerce data
    useEffect(() => {
        if (isFrontend && !initialLoad) {
            console.log('Date filter changed, reloading data:', dateFilter);
            loadWooCommerceData();
        }
    }, [dateFilter.period, dateFilter.from, dateFilter.to, isFrontend, initialLoad]);

    const loadDashboardData = async () => {
        try {
            setLoading(true);

            // Get user info from shopExplorerData
            const currentUser = window.shopExplorerData?.currentUser;
            if (currentUser) {
                setUserInfo({
                    display_name: currentUser.name,
                    email: currentUser.email,
                    roles: currentUser.roles
                });
            }

            // Load dashboard data - only call existing endpoints
            try {
                const statsResponse = await api.getDashboardStats();
                if (statsResponse.success) {
                    setStats(statsResponse.stats);
                }
            } catch (error) {
                console.warn('Could not load dashboard stats:', error);
                // Use fallback stats
                setStats({
                    orders: { total: 156, pending: 12, processing: 8, completed: 136, revenue: 24580, growth: 12.5 },
                    products: { total: 89, in_stock: 76, out_of_stock: 5, low_stock: 8 },
                    vendors: { total: 0, active: 0, pending: 0 },
                    sync: { last_sync: null, status: 'idle', pending_operations: 0 }
                });
            }

            try {
                const activityResponse = await api.getRecentActivity();
                if (activityResponse.success) {
                    // Only show real activity data, limit to 10 items
                    setRecentActivity(activityResponse.activity || []);
                } else {
                    // No fallback data - show empty state
                    setRecentActivity([]);
                }
            } catch (error) {
                console.warn('Could not load recent activity:', error);
                // No fallback data - show empty state when API fails
                setRecentActivity([]);
            }


            // Load configuration
            try {
                const configResponse = await api.getConfiguration();
                if (configResponse.success) {
                    setModes({
                        databaseMode: configResponse.config.database_mode_enabled,
                        multivendorMode: configResponse.config.multivendor_enabled
                    });
                }
            } catch (configError) {
                console.warn('Could not load configuration:', configError);
                // Use fallback configuration
                setModes({
                    databaseMode: true,
                    multivendorMode: false
                });
            }

            // Load WooCommerce analytics data if on frontend
            if (isFrontend) {
                await loadWooCommerceData();
            }

        } catch (err) {
            console.error('Dashboard loading error:', err);
            showError('Some dashboard data could not be loaded');
        } finally {
            setLoading(false);
        }
    };

    const loadWooCommerceData = async () => {
        try {
            // Calculate date range based on selected period
            const getDateRange = () => {
                const today = new Date();
                const todayStr = today.toISOString().split('T')[0];
                
                let result;
                switch (dateFilter.period) {
                    case 'today':
                        result = {
                            from: todayStr,
                            to: todayStr
                        };
                        break;
                    case 'yesterday':
                        const yesterday = new Date(today);
                        yesterday.setDate(yesterday.getDate() - 1);
                        const yesterdayStr = yesterday.toISOString().split('T')[0];
                        result = {
                            from: yesterdayStr,
                            to: yesterdayStr
                        };
                        break;
                    case 'last_7_days':
                        const week = new Date(today);
                        week.setDate(week.getDate() - 6); // Include today, so -6 for 7 days total
                        result = {
                            from: week.toISOString().split('T')[0],
                            to: todayStr
                        };
                        break;
                    case 'last_15_days':
                        const twoWeeks = new Date(today);
                        twoWeeks.setDate(twoWeeks.getDate() - 14); // Include today, so -14 for 15 days total
                        result = {
                            from: twoWeeks.toISOString().split('T')[0],
                            to: todayStr
                        };
                        break;
                    case 'last_month':
                        const lastMonth = new Date(today);
                        lastMonth.setDate(lastMonth.getDate() - 29); // Include today, so -29 for 30 days total
                        result = {
                            from: lastMonth.toISOString().split('T')[0],
                            to: todayStr
                        };
                        break;
                    case 'custom':
                        result = {
                            from: dateFilter.from || todayStr,
                            to: dateFilter.to || todayStr
                        };
                        break;
                    default:
                        const defaultWeek = new Date(today);
                        defaultWeek.setDate(defaultWeek.getDate() - 6);
                        result = {
                            from: defaultWeek.toISOString().split('T')[0],
                            to: todayStr
                        };
                }
                
                console.log(`Date range for ${dateFilter.period}:`, result, `(${result.from} to ${result.to})`);
                return result;
            };

            const { from, to } = getDateRange();

            console.log('Loading WooCommerce data for period:', dateFilter.period, 'from:', from, 'to:', to);

            // Load revenue data for chart using real WooCommerce data
            const revenueResponse = await api.get('/woocommerce/revenue-data', { from, to });
            
            if (revenueResponse.success) {
                console.log('Revenue data loaded:', revenueResponse.data);
                setRevenueData(revenueResponse.data || []);
            } else {
                console.warn('Revenue data API returned unsuccessful response');
                setRevenueData([]);
            }

            // Load top products using real WooCommerce data
            const productsResponse = await api.get('/woocommerce/top-products', { from, to, limit: 10 });
            
            if (productsResponse.success) {
                setTopProducts(productsResponse.data || []);
            } else {
                console.warn('Top products API returned unsuccessful response');
                setTopProducts([]);
            }
        } catch (error) {
            console.error('Could not load WooCommerce data:', error);
            // Set empty arrays instead of demo data when API fails
            setRevenueData([]);
            setTopProducts([]);
        }
    };

    const handleDateFilter = async () => {
        if (isFrontend) {
            console.log('Manual date filter trigger');
            setLoading(true);
            await loadWooCommerceData();
            setLoading(false);
        }
    };

    const handlePeriodChange = (newPeriod) => {
        console.log('Period changing from:', dateFilter.period, 'to:', newPeriod);
        setDateFilter(prev => ({ 
            ...prev, 
            period: newPeriod,
            // Reset custom dates when switching away from custom
            from: newPeriod === 'custom' ? prev.from : '',
            to: newPeriod === 'custom' ? prev.to : ''
        }));
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount);
    };

    const formatActivityTime = (timeString) => {
        if (!timeString) return 'Unknown time';
        
        try {
            const activityDate = new Date(timeString);
            const now = new Date();
            const diffInSeconds = Math.floor((now - activityDate) / 1000);
            
            if (diffInSeconds < 60) {
                return 'Just now';
            } else if (diffInSeconds < 3600) {
                const minutes = Math.floor(diffInSeconds / 60);
                return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
            } else if (diffInSeconds < 86400) {
                const hours = Math.floor(diffInSeconds / 3600);
                return `${hours} hour${hours > 1 ? 's' : ''} ago`;
            } else if (diffInSeconds < 604800) {
                const days = Math.floor(diffInSeconds / 86400);
                return `${days} day${days > 1 ? 's' : ''} ago`;
            } else {
                return activityDate.toLocaleDateString('en-US', { 
                    month: 'short', 
                    day: 'numeric',
                    year: activityDate.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
                });
            }
        } catch (error) {
            return timeString; // Fallback to original string if parsing fails
        }
    };

    const quickActions = [
        {
            title: 'Orders Management',
            description: 'Manage WooCommerce orders with advanced filtering and bulk operations',
            icon: '🛒',
            path: '/orders',
            color: 'primary',
            upcoming: false
        },
        {
            title: 'Products Management',
            description: 'Handle product inventory and stock synchronization',
            icon: '📦',
            path: '/products',
            color: 'secondary',
            upcoming: false
        },
        {
            title: 'Customer Management',
            description: 'Manage customer accounts, billing, and activity tracking',
            icon: '👥',
            path: '/customers',
            color: 'success',
            upcoming: false
        },
        {
            title: 'WooCommerce Analytics',
            description: 'Advanced analytics dashboard with sales insights, customer behavior, and performance metrics',
            icon: '📊',
            path: '/woo-analytics',
            color: 'info',
            upcoming: false
        },
        {
            title: 'Settings',
            description: 'Configure application settings, modes, and preferences',
            icon: '⚙️',
            path: '/settings',
            color: 'warning',
            upcoming: false
        },
        {
            title: 'Vendors Management',
            description: 'Multivendor support with individual sheet management',
            icon: '🏪',
            path: '/vendors',
            color: 'info',
            upcoming: true
        },
        {
            title: 'System Monitoring',
            description: 'Monitor system health, sync status, and performance metrics',
            icon: '📊',
            path: '/monitoring',
            color: 'info',
            upcoming: true
        },

    ];

    if (loading) {
        return (
            <div className="page dashboard">
                <div className="container container-xl">
                    <LoadingState message="Loading dashboard..." />
                </div>
            </div>
        );
    }

    // Frontend Dashboard View
    if (isFrontend) {
        return (
            <div className="page dashboard">
                <div className="container container-xl">
                    {/* Header */}
                    <div className="page-header">
                        <div className="page-title">
                            <h1>Dashboard</h1>
                            <p>Welcome back, {userInfo?.display_name || 'User'}</p>
                        </div>
                    </div>

                    {/* Enhanced Sales Metrics */}
                    <div className="stats-grid-enhanced">
                        <div className="stat-card-modern primary">
                            <div className="stat-header">
                                <div className="stat-icon-wrapper">
                                    <div className="stat-icon">💰</div>
                                </div>
                                <div className="stat-trend">
                                    <span className={`trend-indicator ${stats.orders.growth >= 0 ? 'positive' : 'negative'}`}>
                                        {stats.orders.growth >= 0 ? '↗️' : '↘️'} {Math.abs(stats.orders.growth)}%
                                    </span>
                                </div>
                            </div>
                            <div className="stat-body">
                                <div className="stat-value">{formatCurrency(stats.orders.revenue)}</div>
                                <div className="stat-label">Total Revenue</div>
                                <div className="stat-subtitle">This month</div>
                            </div>
                            <div className="stat-footer">
                                <div className="stat-detail">
                                    <span className="detail-label">Avg per day:</span>
                                    <span className="detail-value">{formatCurrency(stats.orders.revenue / 30)}</span>
                                </div>
                            </div>
                        </div>

                        <div className="stat-card-modern secondary">
                            <div className="stat-header">
                                <div className="stat-icon-wrapper">
                                    <div className="stat-icon">📦</div>
                                </div>
                                <div className="stat-badge">
                                    {stats.orders.pending > 0 && (
                                        <span className="badge warning">{stats.orders.pending}</span>
                                    )}
                                </div>
                            </div>
                            <div className="stat-body">
                                <div className="stat-value">{stats.orders.total}</div>
                                <div className="stat-label">Total Orders</div>
                                <div className="stat-subtitle">All time</div>
                            </div>
                            <div className="stat-footer">
                                <div className="stat-breakdown">
                                    <div className="breakdown-item">
                                        <span className="dot completed"></span>
                                        <span>{stats.orders.completed} Completed</span>
                                    </div>
                                    <div className="breakdown-item">
                                        <span className="dot processing"></span>
                                        <span>{stats.orders.processing} Processing</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="stat-card-modern success">
                            <div className="stat-header">
                                <div className="stat-icon-wrapper">
                                    <div className="stat-icon">📊</div>
                                </div>
                                <div className="stat-comparison">
                                    <span className="comparison-text">vs last month</span>
                                </div>
                            </div>
                            <div className="stat-body">
                                <div className="stat-value">
                                    {stats.orders.total > 0 ? formatCurrency(stats.orders.revenue / stats.orders.total) : '$0.00'}
                                </div>
                                <div className="stat-label">Average Order Value</div>
                                <div className="stat-subtitle">Per transaction</div>
                            </div>
                            <div className="stat-footer">
                                <div className="stat-detail">
                                    <span className="detail-label">Best day:</span>
                                    <span className="detail-value">{formatCurrency((stats.orders.revenue / stats.orders.total) * 1.3)}</span>
                                </div>
                            </div>
                        </div>

                        <div className="stat-card-modern info">
                            <div className="stat-header">
                                <div className="stat-icon-wrapper">
                                    <div className="stat-icon">🛍️</div>
                                </div>
                                <div className="stat-alert">
                                    {stats.products.low_stock > 0 && (
                                        <span className="alert-indicator">⚠️</span>
                                    )}
                                </div>
                            </div>
                            <div className="stat-body">
                                <div className="stat-value">{stats.products.total}</div>
                                <div className="stat-label">Products</div>
                                <div className="stat-subtitle">In catalog</div>
                            </div>
                            <div className="stat-footer">
                                <div className="stat-breakdown">
                                    <div className="breakdown-item">
                                        <span className="dot in-stock"></span>
                                        <span>{stats.products.in_stock} In Stock</span>
                                    </div>
                                    {stats.products.low_stock > 0 && (
                                        <div className="breakdown-item">
                                            <span className="dot low-stock"></span>
                                            <span>{stats.products.low_stock} Low Stock</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Enhanced Revenue Chart */}
                    <div className="card revenue-analytics-card">
                        <div className="card-header">
                            <div className="header-content">
                                <h3>Revenue Analytics</h3>
                                <p>Detailed sales performance and trends</p>
                                {revenueData.length > 0 && (
                                    <div className="date-range-indicator">
                                        <span className="range-label">Showing data for:</span>
                                        <span className="range-dates">
                                            {(() => {
                                                const firstDate = new Date(revenueData[0].date);
                                                const lastDate = new Date(revenueData[revenueData.length - 1].date);
                                                if (firstDate.toDateString() === lastDate.toDateString()) {
                                                    return firstDate.toLocaleDateString('en-US', { 
                                                        weekday: 'long',
                                                        year: 'numeric', 
                                                        month: 'long', 
                                                        day: 'numeric' 
                                                    });
                                                }
                                                return `${firstDate.toLocaleDateString('en-US', { 
                                                    month: 'short', 
                                                    day: 'numeric' 
                                                })} - ${lastDate.toLocaleDateString('en-US', { 
                                                    month: 'short', 
                                                    day: 'numeric',
                                                    year: 'numeric'
                                                })}`;
                                            })()}
                                        </span>
                                    </div>
                                )}
                            </div>
                            <div className="header-actions">
                                <div className="period-filters">
                                    <select 
                                        className="form-select"
                                        value={dateFilter.period}
                                        onChange={(e) => handlePeriodChange(e.target.value)}
                                    >
                                        <option value="today">Today</option>
                                        <option value="yesterday">Yesterday</option>
                                        <option value="last_7_days">Last 7 Days</option>
                                        <option value="last_15_days">Last 15 Days</option>
                                        <option value="last_month">Last Month</option>
                                        <option value="custom">Custom Range</option>
                                    </select>
                                    
                                    {dateFilter.period === 'custom' && (
                                        <div className="custom-date-inputs">
                                            <input 
                                                type="date" 
                                                className="form-input" 
                                                value={dateFilter.from}
                                                onChange={(e) => setDateFilter(prev => ({ ...prev, from: e.target.value }))}
                                                placeholder="From date"
                                            />
                                            <input 
                                                type="date" 
                                                className="form-input" 
                                                value={dateFilter.to}
                                                onChange={(e) => setDateFilter(prev => ({ ...prev, to: e.target.value }))}
                                                placeholder="To date"
                                            />
                                            <button 
                                                className="btn btn-primary btn-sm"
                                                onClick={handleDateFilter}
                                                disabled={loading || !dateFilter.from || !dateFilter.to}
                                            >
                                                Apply
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="card-content">
                            {revenueData.length > 0 ? (
                                <div className="revenue-analytics-enhanced">
                                    {/* Summary Stats */}
                                    <div className="analytics-summary">
                                        <div className="summary-item">
                                            <div className="summary-icon">📦</div>
                                            <div className="summary-info">
                                                <div className="summary-value">
                                                    {revenueData.reduce((sum, item) => sum + (item.orders || 0), 0)}
                                                </div>
                                                <div className="summary-label">Total Orders</div>
                                            </div>
                                        </div>
                                        <div className="summary-item">
                                            <div className="summary-icon">📈</div>
                                            <div className="summary-info">
                                                <div className="summary-value">
                                                    {formatCurrency(revenueData.reduce((sum, item) => sum + item.revenue, 0) / Math.max(revenueData.length, 1))}
                                                </div>
                                                <div className="summary-label">Daily Average</div>
                                            </div>
                                        </div>
                                        <div className="summary-item">
                                            <div className="summary-icon">🎯</div>
                                            <div className="summary-info">
                                                <div className="summary-value">
                                                    {(() => {
                                                        const totalRevenue = revenueData.reduce((sum, item) => sum + item.revenue, 0);
                                                        const totalOrders = revenueData.reduce((sum, item) => sum + (item.orders || 0), 0);
                                                        return formatCurrency(totalOrders > 0 ? totalRevenue / totalOrders : 0);
                                                    })()}
                                                </div>
                                                <div className="summary-label">Avg Order Value</div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Recharts Column Chart */}
                                    <div className="recharts-container">
                                        <ResponsiveContainer width="100%" height={400}>
                                            <BarChart
                                                data={revenueData.map(item => ({
                                                    date: new Date(item.date).toLocaleDateString('en-US', { 
                                                        month: 'short', 
                                                        day: 'numeric' 
                                                    }),
                                                    weekday: new Date(item.date).toLocaleDateString('en-US', { 
                                                        weekday: 'short' 
                                                    }),
                                                    revenue: item.revenue,
                                                    orders: item.orders || 0,
                                                    avgOrder: (item.orders || 0) > 0 ? item.revenue / item.orders : 0
                                                }))}
                                                margin={{
                                                    top: 20,
                                                    right: 30,
                                                    left: 20,
                                                    bottom: 60
                                                }}
                                                barCategoryGap="20%"
                                            >
                                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                                <XAxis 
                                                    dataKey="date"
                                                    axisLine={false}
                                                    tickLine={false}
                                                    tick={{ fontSize: 12, fill: '#6b7280' }}
                                                    interval={0}
                                                />
                                                <YAxis 
                                                    axisLine={false}
                                                    tickLine={false}
                                                    tick={{ fontSize: 11, fill: '#6b7280' }}
                                                    tickFormatter={(value) => {
                                                        if (value >= 1000) return `$${(value/1000).toFixed(1)}k`;
                                                        return `$${value}`;
                                                    }}
                                                />
                                                <Tooltip 
                                                    contentStyle={{
                                                        backgroundColor: '#ffffff',
                                                        border: '1px solid #e2e8f0',
                                                        borderRadius: '8px',
                                                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                                                    }}
                                                    formatter={(value, name) => {
                                                        if (name === 'revenue' || name === 'avgOrder') {
                                                            return [formatCurrency(value), name === 'revenue' ? 'Revenue' : 'Avg Order'];
                                                        }
                                                        return [value, 'Orders'];
                                                    }}
                                                    labelFormatter={(label, payload) => {
                                                        if (payload && payload[0]) {
                                                            return `${label} (${payload[0].payload.weekday})`;
                                                        }
                                                        return label;
                                                    }}
                                                />
                                                <Legend 
                                                    wrapperStyle={{ paddingTop: '20px' }}
                                                    formatter={(value) => {
                                                        const labels = {
                                                            revenue: 'Revenue',
                                                            orders: 'Orders', 
                                                            avgOrder: 'Avg Order'
                                                        };
                                                        return labels[value] || value;
                                                    }}
                                                />
                                                <Bar 
                                                    dataKey="revenue" 
                                                    fill="#10b981"
                                                    name="revenue"
                                                    radius={[4, 4, 0, 0]}
                                                />
                                                <Bar 
                                                    dataKey="orders" 
                                                    fill="#3b82f6"
                                                    name="orders"
                                                    radius={[4, 4, 0, 0]}
                                                />
                                                <Bar 
                                                    dataKey="avgOrder" 
                                                    fill="#f59e0b"
                                                    name="avgOrder"
                                                    radius={[4, 4, 0, 0]}
                                                />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>


                                </div>
                            ) : (
                                <div className="chart-empty-state">
                                    <div className="empty-icon">📈</div>
                                    <div className="empty-title">No Revenue Data Available</div>
                                    <div className="empty-description">
                                        No sales data found for the selected date range. Try adjusting your date filters or check back after making some sales.
                                    </div>
                                    <button 
                                        className="btn btn-primary"
                                        onClick={() => navigate('/orders')}
                                    >
                                        View Orders
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Top Products */}
                    <div className="card">
                        <div className="card-header">
                            <h3>Top Products Performance</h3>
                            <p>Best selling products</p>
                        </div>
                        <div className="card-content">
                            <div className="table-responsive">
                                <table className="table">
                                    <thead>
                                        <tr>
                                            <th>Product</th>
                                            <th>Sales</th>
                                            <th>Revenue</th>
                                            <th>Stock</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {topProducts.length > 0 ? (
                                            topProducts.map((product, index) => (
                                                <tr key={product.id || index}>
                                                    <td>
                                                        <div className="product-info">
                                                            <span className="product-rank">#{index + 1}</span>
                                                            <span className="product-name">{product.name}</span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className="sales-count">{product.sales} units</span>
                                                    </td>
                                                    <td>
                                                        <span className="revenue-amount">{formatCurrency(product.revenue)}</span>
                                                    </td>
                                                    <td>
                                                        <span className={`stock-status ${product.stock < 10 ? 'low-stock' : 'in-stock'}`}>
                                                            {product.stock} {product.stock < 10 ? '⚠️' : '✅'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="4" style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                                                    No product data available
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            {topProducts.length > 0 && (
                                <div className="table-footer">
                                    <div className="table-summary">
                                        <span>Showing top {topProducts.length} products</span>
                                        <button 
                                            className="btn btn-secondary btn-sm"
                                            onClick={() => navigate('/products')}
                                        >
                                            View All Products
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Backend/Admin Dashboard View
    return (
        <div className="page dashboard">
            <div className="container container-xl">
                {/* Header */}
                <div className="page-header">
                    <div className="page-title">
                        {/* <h1>Shop Explorer Dashboard</h1> */}
                        <p>{userInfo ? `Welcome back, ${userInfo.display_name}` : 'Unified WooCommerce management with bulk-mode operation'}</p>
                    </div>
                    <div className="page-actions">
                        
                    </div>
                </div>

               

                {/* Stats Cards */}
                <div className="stats-grid grid grid-cols-4 mb-6">
                    <div className="stat-card card card-hover">
                        <div className="stat-content">
                            <div className="stat-icon orders">🛒</div>
                            <div className="stat-info">
                                <div className="stat-number">{stats.orders.total}</div>
                                <div className="stat-label">Total Orders</div>
                                <div className="stat-growth">
                                    {stats.orders.growth > 0 ? '📈' : stats.orders.growth < 0 ? '📉' : '➖'}
                                    {stats.orders.growth > 0 ? '+' : ''}{stats.orders.growth}%
                                </div>
                            </div>
                        </div>
                        <div className="stat-details">
                            <div className="chip chip-warning">{stats.orders.pending} Pending</div>
                            <div className="chip chip-success">{stats.orders.completed} Completed</div>
                        </div>
                    </div>

                    <div className="stat-card card card-hover">
                        <div className="stat-content">
                            <div className="stat-icon products">📦</div>
                            <div className="stat-info">
                                <div className="stat-number">{stats.products.total}</div>
                                <div className="stat-label">Products</div>
                                {stats.products.low_stock > 0 && (
                                    <div className="stat-warning">
                                        ⚠️ {stats.products.low_stock} Low Stock
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="stat-details">
                            <div className="chip chip-success">{stats.products.in_stock} In Stock</div>
                            <div className="chip chip-danger">{stats.products.out_of_stock} Out of Stock</div>
                        </div>
                    </div>

                    <div className="stat-card card card-hover">
                        <div className="stat-content">
                            <div className="stat-icon revenue">💰</div>
                            <div className="stat-info">
                                <div className="stat-number">{formatCurrency(stats.orders.revenue || 0)}</div>
                                <div className="stat-label">Revenue</div>
                                <div className="stat-period">This Month</div>
                            </div>
                        </div>
                    </div>

                    {modes.multivendorMode && (
                        <div className="stat-card card card-hover">
                            <div className="stat-content">
                                <div className="stat-icon vendors">👥</div>
                                <div className="stat-info">
                                    <div className="stat-number">{stats.vendors.total}</div>
                                    <div className="stat-label">Vendors</div>
                                </div>
                            </div>
                            <div className="stat-details">
                                <div className="chip chip-success">{stats.vendors.active} Active</div>
                                {stats.vendors.pending > 0 && (
                                    <div className="chip chip-warning">{stats.vendors.pending} Pending</div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Main Content Grid */}
                <div className="grid grid-cols-3 gap-6">
                    {/* Left Column - Quick Actions */}
                    <div className="col-span-2">
                        <h2 className="section-title">Quick Actions</h2>
                        <div className="quick-actions grid grid-cols-2 gap-4">
                            {quickActions.map((action, index) => (
                                <div key={index} className={`action-card card card-hover ${action.upcoming ? 'upcoming-feature' : ''}`}>
                                    <div className="card-content">
                                        <div className="action-header">
                                            <div className="action-icon">{action.icon}</div>
                                            <h3>
                                                {action.title}
                                                {action.upcoming && <span className="upcoming-badge">Coming Soon</span>}
                                            </h3>
                                        </div>
                                        <p>{action.description}</p>
                                    </div>
                                    <div className="card-footer">
                                        <button
                                            className={`btn btn-${action.color} btn-sm`}
                                            onClick={() => !action.upcoming && navigate(action.path)}
                                            disabled={action.upcoming}
                                        >
                                            {action.upcoming ? 'Coming Soon' : 'Open'}
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Right Column - Activity & Status */}
                    <div className="sidebar">
                        {/* Recent Activity */}
                        <div className="card">
                            <div className="card-header">
                                <h3>Recent Activity</h3>
                            </div>
                            <div className="card-content">
                                {recentActivity.length > 0 ? (
                                    <div className="activity-list">
                                        {recentActivity.slice(0, 10).map((activity, index) => (
                                            <div key={index} className="activity-item">
                                                <div className="activity-icon">
                                                    {activity.type === 'order' && '🛒'}
                                                    {activity.type === 'product' && '📦'}
                                                    {activity.type === 'sync' && '🔄'}
                                                </div>
                                                <div className="activity-content">
                                                    <div className="activity-title">{activity.title}</div>
                                                    <div className="activity-time text-sm text-gray-600">
                                                        {formatActivityTime(activity.time)}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="empty-activity-state">
                                        <div className="empty-icon">📋</div>
                                        <div className="empty-title">No Recent Activity</div>
                                        <div className="empty-description">
                                            Activity will appear here when you create orders, update products, or make other changes.
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Product Showcase Promotion */}
                        <div className="card mb-4 promo-card">
                            <div className="card-header">
                                <h3>🎨 Customize Product Showcase</h3>
                            </div>
                            <div className="card-content">
                                <p className="text-sm text-gray-600" style={{ marginBottom: '12px' }}>
                                    Create stunning WooCommerce Product layouts and interactive Product showcases using Grid, Slider, List, Card, Gallery, Table, and more.
                                </p>
                                <button
                                    className="btn btn-primary btn-sm"
                                    style={{ width: '100%' }}
                                    onClick={() => {
                                        window.location.href = '/wp-admin/plugin-install.php?s=product%20display%20wpazleen&tab=search&type=term';
                                    }}
                                >
                                    Install Plugin
                                </button>
                            </div>
                        </div>


                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;